import _ from 'lodash'

const NO_LEVEL_GROUP_KEY = '__no_level__'
const NO_LEVEL_GROUP_LABEL = 'No Level Assigned'
const NO_COMPANY_GROUP_KEY = '__no_company__'
const NO_COMPANY_GROUP_LABEL = 'No Company Assigned'
const INVALID_AREA_GROUP_KEY = '__invalid_area__'
const INVALID_AREA_GROUP_LABEL = 'Inactive Area'

/**
 * Determines if a figure has an invalid/unassigned area.
 * Matches: null, undefined, empty string, and the literal "#N/A".
 * @param {object} figure - A professional figure object
 * @returns {boolean}
 */
function hasInvalidArea(figure) {
  const area = figure.area
  if (!area || area === '' || area === '#N/A') return true
  return false
}

/**
 * Groups professional figures with smart hierarchy:
 * - If all figures belong to the same company → flat grouping by Internal Level only
 * - If figures span multiple companies → hierarchical grouping: Company → Internal Level
 *
 * For internal figures (externalFilter === 0), figures with invalid area (#N/A, null, empty)
 * are separated into a dedicated section at the bottom with its own Company → Level subgrouping.
 * This section is intended to start collapsed in the UI.
 *
 * Preserves the original array index so that dispatch operations (set/delete) still
 * target the correct figure in the Redux store.
 *
 * @param {Array} figures - The full array of professional figures from Redux state
 * @param {number} externalFilter - 0 for internal figures, 1 for external figures
 * @returns {Object} Result object with shape:
 *   {
 *     isHierarchical: boolean,
 *     groups: Array<{ key, label, figures, subgroups }>,
 *     invalidAreaGroup: { key, label, figures, subgroups, isHierarchical } | null,
 *     newFigures: Array<{ index: number, figure: object }>
 *   }
 */
export const groupFiguresByLevel = (figures, externalFilter) => {
  const indexed = figures
    .map((figure, index) => ({ index, figure }))
    .filter(({ figure }) => figure.external === externalFilter)

  if (indexed.length === 0) {
    return { isHierarchical: false, groups: [], invalidAreaGroup: null, newFigures: [] }
  }

  // Separate new figures (not yet saved) — they stay ungrouped and always visible
  var newFigures = indexed.filter(({ figure }) => !figure.fromDB)
  var savedFigures = indexed.filter(({ figure }) => figure.fromDB)

  // For internal figures, separate invalid-area figures into their own bucket
  var validFigures = savedFigures
  var invalidAreaFigures = []

  if (externalFilter === 0) {
    validFigures = savedFigures.filter(({ figure }) => !hasInvalidArea(figure))
    invalidAreaFigures = savedFigures.filter(({ figure }) => hasInvalidArea(figure))
  }

  // Build main groups from valid figures
  var mainResult = buildGroups(validFigures)

  // Build invalid area group with its own hierarchy
  var invalidAreaGroup = null
  if (invalidAreaFigures.length > 0) {
    var invalidResult = buildGroups(invalidAreaFigures)
    invalidAreaGroup = {
      key: INVALID_AREA_GROUP_KEY,
      label: INVALID_AREA_GROUP_LABEL,
      figures: invalidAreaFigures,
      subgroups: invalidResult.groups,
      isHierarchical: invalidResult.isHierarchical,
    }
  }

  return {
    isHierarchical: mainResult.isHierarchical,
    groups: mainResult.groups,
    invalidAreaGroup: invalidAreaGroup,
    newFigures: newFigures,
  }
}

/**
 * Builds the group structure from a set of indexed figures.
 * Determines whether to use flat (single company) or hierarchical (multi company) grouping.
 * @param {Array} indexed - Array of { index, figure } objects
 * @returns {{ isHierarchical: boolean, groups: Array }}
 */
function buildGroups(indexed) {
  if (indexed.length === 0) {
    return { isHierarchical: false, groups: [] }
  }

  var uniqueCompanies = _.uniqBy(indexed, ({ figure }) => figure.company_code || NO_COMPANY_GROUP_KEY)
  var isMultiCompany = uniqueCompanies.length > 1

  if (!isMultiCompany) {
    return { isHierarchical: false, groups: groupByLevel(indexed) }
  }

  var groupedByCompany = _.groupBy(indexed, ({ figure }) => {
    if (!figure.company_code && !figure.company) return NO_COMPANY_GROUP_KEY
    return figure.company_code || NO_COMPANY_GROUP_KEY
  })

  var companyGroups = Object.keys(groupedByCompany)
    .filter(key => key !== NO_COMPANY_GROUP_KEY)
    .sort()
    .map(companyCode => {
      var companyFigures = groupedByCompany[companyCode]
      var companyName = companyFigures[0].figure.company || companyCode
      return {
        key: companyCode,
        label: companyName,
        figures: companyFigures,
        subgroups: groupByLevel(companyFigures),
      }
    })

  if (groupedByCompany[NO_COMPANY_GROUP_KEY]) {
    var noCompanyFigures = groupedByCompany[NO_COMPANY_GROUP_KEY]
    companyGroups.push({
      key: NO_COMPANY_GROUP_KEY,
      label: NO_COMPANY_GROUP_LABEL,
      figures: noCompanyFigures,
      subgroups: groupByLevel(noCompanyFigures),
    })
  }

  return { isHierarchical: true, groups: companyGroups }
}

/**
 * Computes the default collapsed state for compact mode.
 * Companies (top-level groups in hierarchical mode) are EXPANDED.
 * All level subgroups within each company are COLLAPSED.
 * The invalid area section is COLLAPSED.
 *
 * Uses composite keys (companyKey_levelKey) for nested levels so that
 * toggling a level in one company does not affect the same-named level
 * in another company.
 *
 * @param {Object} groupResult - The result of groupFiguresByLevel()
 * @returns {Object} Map of groupKey → boolean (true = collapsed)
 */
export const getDefaultCollapsedKeys = (groupResult) => {
  var collapsed = {}

  if (!groupResult) return collapsed

  // Invalid area section always starts collapsed
  if (groupResult.invalidAreaGroup) {
    collapsed[INVALID_AREA_GROUP_KEY] = true

    // All sub-levels inside invalid area are also collapsed
    if (groupResult.invalidAreaGroup.subgroups) {
      groupResult.invalidAreaGroup.subgroups.forEach(function (sub) {
        if (groupResult.invalidAreaGroup.isHierarchical) {
          // sub is a company group inside invalid area — company expanded, levels collapsed
          collapsed['ia_' + sub.key] = false
          if (sub.subgroups) {
            sub.subgroups.forEach(function (levelGroup) {
              collapsed['ia_' + sub.key + '_' + levelGroup.key] = true
            })
          }
        } else {
          // sub is a level group directly under invalid area
          collapsed['ia_' + sub.key] = true
        }
      })
    }
  }

  if (groupResult.isHierarchical) {
    // Multi-company: companies are EXPANDED, levels within are COLLAPSED
    groupResult.groups.forEach(function (companyGroup) {
      // Company itself is NOT collapsed (expanded) — don't add to map or set false
      collapsed[companyGroup.key] = false
      if (companyGroup.subgroups) {
        companyGroup.subgroups.forEach(function (levelGroup) {
          collapsed[companyGroup.key + '_' + levelGroup.key] = true
        })
      }
    })
  } else {
    // Single company (flat): all level groups are COLLAPSED
    groupResult.groups.forEach(function (levelGroup) {
      collapsed[levelGroup.key] = true
    })
  }

  return collapsed
}

/**
 * Groups an already-filtered array of indexed figures by their internal level.
 * Sorted by level_id ascending (lower grade first).
 * @param {Array} indexed - Array of { index, figure } objects
 * @returns {Array} Sorted groups with "No Level" at the bottom
 */
function groupByLevel(indexed) {
  var grouped = _.groupBy(indexed, ({ figure }) => {
    var level = figure.level
    if (!level || level === '') {
      return NO_LEVEL_GROUP_KEY
    }
    return level
  })

  var groups = Object.keys(grouped)
    .filter(key => key !== NO_LEVEL_GROUP_KEY)
    .sort((a, b) => {
      var aId = grouped[a][0].figure.level_id
      var bId = grouped[b][0].figure.level_id
      if (aId === null || aId === undefined || aId === -1) return 1
      if (bId === null || bId === undefined || bId === -1) return -1
      return aId - bId
    })
    .map(key => ({
      key: key,
      label: key,
      figures: grouped[key],
      subgroups: null,
    }))

  if (grouped[NO_LEVEL_GROUP_KEY]) {
    groups.push({
      key: NO_LEVEL_GROUP_KEY,
      label: NO_LEVEL_GROUP_LABEL,
      figures: grouped[NO_LEVEL_GROUP_KEY],
      subgroups: null,
    })
  }

  return groups
}
